import { useState, useEffect } from "react";
import PiCheckTickCircleSolid from "@markeroo/icons/src/solid/general/PiCheckTickCircleSolid";

import { Modal, ModalHeader, ModalBody, ModalFooter } from "@/components/Modal";
import { useGCCourses, useImportGCCourse } from "@/api/hooks";
import type { GCCourse } from "@/api/hooks";
import { useNotification } from "@/providers/NotificationProvider";
import { mutedText } from "@/styles/variants";
import { buttonVariants } from "@markeroo/theme";
import { ui, notifications } from "@/strings";
import { useGCTokenExpiration } from "@/features/google-classroom";

interface GCCourseImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportSuccess?: () => void;
}

export function GCCourseImportModal({
  isOpen,
  onClose,
  onImportSuccess,
}: GCCourseImportModalProps) {
  const { data: courses, isLoading, isError, error } = useGCCourses({ enabled: isOpen });
  const importMutation = useImportGCCourse();
  const { show } = useNotification();
  const { isGCTokenExpired, handleGCTokenExpired } = useGCTokenExpiration();
  const [selectedCourseIds, setSelectedCourseIds] = useState<Set<string>>(new Set());
  const [importingCourseId, setImportingCourseId] = useState<string | null>(null);

  // Track if we've detected token expiration to avoid rendering error UI
  const [tokenExpiredHandled, setTokenExpiredHandled] = useState(false);

  // Handle token expiration when loading courses fails
  useEffect(() => {
    if (isError && isGCTokenExpired(error) && !tokenExpiredHandled) {
      setTokenExpiredHandled(true);
      handleGCTokenExpired("course_list");
      onClose();
    }
  }, [isError, error, isGCTokenExpired, handleGCTokenExpired, onClose, tokenExpiredHandled]);

  // Reset token expired state when modal is reopened
  useEffect(() => {
    if (isOpen) {
      setTokenExpiredHandled(false);
    }
  }, [isOpen]);

  const availableCourses = courses?.filter((c) => !c.already_imported) ?? [];
  const importedCourses = courses?.filter((c) => c.already_imported) ?? [];

  // Don't render error UI if we're handling token expiration
  const showError = isError && !tokenExpiredHandled && !isGCTokenExpired(error);

  const handleToggleCourse = (courseId: string) => {
    setSelectedCourseIds((prev) => {
      const next = new Set(prev);
      if (next.has(courseId)) {
        next.delete(courseId);
      } else {
        next.add(courseId);
      }
      return next;
    });
  };

  const handleImportSelected = async () => {
    const courseIds = Array.from(selectedCourseIds);
    let successCount = 0;

    for (const courseId of courseIds) {
      setImportingCourseId(courseId);
      try {
        await importMutation.mutateAsync(courseId);
        successCount++;
        // Remove from selection after successful import
        setSelectedCourseIds((prev) => {
          const next = new Set(prev);
          next.delete(courseId);
          return next;
        });
      } catch (importError) {
        if (isGCTokenExpired(importError)) {
          handleGCTokenExpired("course_import");
          onClose();
          return; // Stop importing on token expiration
        }
        show(notifications.googleClassroom.courseImportFailed, "error");
      }
    }

    setImportingCourseId(null);

    if (successCount > 0) {
      show(notifications.googleClassroom.coursesImported(successCount), "success");
      onImportSuccess?.();
    }

    // Close modal if all courses imported successfully
    if (successCount === courseIds.length) {
      onClose();
    }
  };

  const handleClose = () => {
    setSelectedCourseIds(new Set());
    setImportingCourseId(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalHeader
        title={ui.googleClassroom.importCourses}
        onClose={handleClose}
      />
      <ModalBody>
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-pulse text-text-theme-muted">{ui.loading.default}</div>
          </div>
        )}

        {showError && (
          <>
            {/* eslint-disable-next-line no-restricted-syntax -- Intentional red for error state */}
            <div className="rounded-lg border border-red-200 bg-semantic-error-bg p-4 text-semantic-error-hover">
              {notifications.googleClassroom.loadCoursesFailed}
            </div>
          </>
        )}

        {!isLoading && !showError && availableCourses.length === 0 && importedCourses.length === 0 && (
          <div className="rounded-lg border border-border bg-surface-secondary p-8 text-center">
            <p className="text-text-theme-secondary">{ui.googleClassroom.noCourses}</p>
            <p className={`mt-2 ${mutedText}`}>{ui.googleClassroom.noCoursesDescription}</p>
          </div>
        )}

        {!isLoading && !showError && (availableCourses.length > 0 || importedCourses.length > 0) && (
          <div className="space-y-4">
            <p className={mutedText}>{ui.googleClassroom.selectCoursesDescription}</p>

            {availableCourses.length > 0 && (
              <div className="space-y-2">
                {availableCourses.map((course) => (
                  <CourseItem
                    key={course.id}
                    course={course}
                    isSelected={selectedCourseIds.has(course.id)}
                    isImporting={importingCourseId === course.id}
                    onToggle={() => handleToggleCourse(course.id)}
                  />
                ))}
              </div>
            )}

            {importedCourses.length > 0 && (
              <>
                <div className="my-4 border-t border-border" />
                <p className="text-sm font-medium text-text-theme-muted">
                  {ui.googleClassroom.alreadyImported}
                </p>
                <div className="space-y-2 opacity-60">
                  {importedCourses.map((course) => (
                    <CourseItem
                      key={course.id}
                      course={course}
                      isSelected={false}
                      isImporting={false}
                      disabled
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <button
          type="button"
          className={buttonVariants.secondary}
          onClick={handleClose}
        >
          {ui.actions.cancel}
        </button>
        <button
          type="button"
          className={buttonVariants.primary}
          onClick={() => void handleImportSelected()}
          disabled={selectedCourseIds.size === 0 || importMutation.isPending}
        >
          {importMutation.isPending
            ? ui.googleClassroom.importingCourses
            : ui.googleClassroom.importSelected}
        </button>
      </ModalFooter>
    </Modal>
  );
}

interface CourseItemProps {
  course: GCCourse;
  isSelected: boolean;
  isImporting: boolean;
  disabled?: boolean;
  onToggle?: () => void;
}

function CourseItem({
  course,
  isSelected,
  isImporting,
  disabled = false,
  onToggle,
}: CourseItemProps) {
  return (
    <label
      className={`flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors ${
        disabled
          ? "cursor-default border-border bg-surface-secondary"
          : isSelected
            ? "border-primary bg-brand-50"
            : "border-border hover:border-border hover:bg-surface-secondary"
      }`}
    >
      {!disabled && (
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggle}
          disabled={isImporting}
          className="mt-1 h-4 w-4 rounded border-border text-primary focus:ring-primary"
        />
      )}
      {disabled && (
        <PiCheckTickCircleSolid size={20} className="mt-0.5 flex-shrink-0 text-semantic-success" />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-text-theme-primary truncate">{course.name}</span>
          {isImporting && (
            <span className="text-xs text-primary">{ui.googleClassroom.importing}</span>
          )}
        </div>
        {course.section && (
          <p className="mt-0.5 text-sm text-text-theme-muted truncate">
            {/* eslint-disable-next-line no-restricted-syntax */}
            {ui.googleClassroom.courseSection}: {course.section}
          </p>
        )}
        {!disabled && (
          <p className="mt-1 text-xs text-text-theme-muted">
            {ui.googleClassroom.studentsWillSync}
          </p>
        )}
      </div>
    </label>
  );
}
